import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { RequireAuth, RequirePermission } from '@/components/RequirePermission';
import { LoginPage } from '@/pages/Login';
import { AcceptInvitePage } from '@/pages/AcceptInvite';
import { DashboardPage } from '@/pages/Dashboard';
import { InvoiceMailboxPage } from '@/pages/InvoiceMailbox';
import { InvoicesPage } from '@/pages/Invoices';
import { InvoiceDetailPage } from '@/pages/InvoiceDetail';
import { ApprovalDetailPage, ApprovalsPage } from '@/pages/Approvals';
import { AccountingPage } from '@/pages/Accounting';
import { FinanceRequestsPage } from '@/pages/FinanceRequests';
import { PayablesPage } from '@/pages/Payables';
import { PayrollDetailPage, PayrollImportPage, PayrollPage } from '@/pages/Payroll';
import { EmployeesPage } from '@/pages/hr/Employees';
import { PaymentBatchDetailPage, PaymentBatchesPage, PaymentQueuePage } from '@/pages/Payments';
import { BankStatementsPage, BankTransactionsPage } from '@/pages/Banking';
import { ReconciliationPage } from '@/pages/Reconciliation';
import { ReportsPage } from '@/pages/Reports';
import { AuditPage } from '@/pages/Audit';
import { NotificationsPage } from '@/pages/Notifications';
import { AccountPage } from '@/pages/Account';
import { CookbookPage } from '@/pages/Cookbook';
import { SystemSettingsPage } from '@/pages/settings/SystemSettings';
import { VendorInvoicesPage } from '@/pages/VendorInvoices';
import { useAuth } from '@/hooks/useAuth';
import { homePath, isVendorLogin } from '@/lib/navigation';
import {
  BusinessUnitsPage,
  CompaniesPage,
  DepartmentsPage,
  GroupsPage,
  LocationsPage,
  RegionsPage,
  UsersPage,
  VendorsPage,
  VerticalsPage,
} from '@/pages/settings';
import { ApprovalRulesPage } from '@/pages/settings/ApprovalRules';
import { RolesPage } from '@/pages/settings/Roles';
import { PaymentAccountsPage } from '@/pages/settings/PaymentAccounts';
import { LedgersPage } from '@/pages/settings/Ledgers';
import { TdsSectionsPage } from '@/pages/settings/TdsSections';

/**
 * Routes mirror PRD §36 exactly. Each is wrapped in the permission its screen
 * needs, so a user who navigates directly to a URL sees a clear message rather
 * than a screen full of 403s.
 */
export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />

      <Route
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<HomeRedirect />} />

        <Route
          path="/dashboard"
          element={
            <RequirePermission permissions={['dashboard:read']}>
              <DashboardPage />
            </RequirePermission>
          }
        />

        <Route
          path="/vendor/invoices"
          element={
            <RequirePermission permissions={['vendor_portal:read']}>
              <VendorInvoicesPage />
            </RequirePermission>
          }
        />

        <Route
          path="/invoices"
          element={
            <RequirePermission permissions={['invoice:read']}>
              <InvoicesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/invoices/review"
          element={
            <RequirePermission permissions={['invoice:read']}>
              <InvoicesPage initialView="REVIEW" />
            </RequirePermission>
          }
        />
        <Route
          path="/integrations/outlook"
          element={
            <RequirePermission permissions={['mail_connection:manage', 'mail_connection:read_all']}>
              <InvoiceMailboxPage />
            </RequirePermission>
          }
        />
        <Route
          path="/invoices/:id/approval"
          element={
            <RequirePermission permissions={['invoice:read']}>
              <InvoiceDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/invoices/:id"
          element={
            <RequirePermission permissions={['invoice:read']}>
              <InvoiceDetailPage />
            </RequirePermission>
          }
        />

        <Route
          path="/approvals"
          element={
            <RequirePermission permissions={['approval:read', 'approval:read_all']}>
              <ApprovalsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/approvals/:id"
          element={
            <RequirePermission permissions={['approval:read', 'approval:read_all']}>
              <ApprovalDetailPage />
            </RequirePermission>
          }
        />

        <Route
          path="/accounting"
          element={
            <RequirePermission permissions={['invoice:verify']}>
              <AccountingPage />
            </RequirePermission>
          }
        />
        <Route
          path="/accounting/:id"
          element={
            <RequirePermission permissions={['invoice:verify']}>
              <AccountingPage />
            </RequirePermission>
          }
        />
        <Route
          path="/finance-requests"
          element={
            <RequirePermission permissions={['finance_request:read', 'finance_request:read_all']}>
              <FinanceRequestsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/finance-requests/:id"
          element={
            <RequirePermission permissions={['finance_request:read', 'finance_request:read_all']}>
              <FinanceRequestsPage />
            </RequirePermission>
          }
        />

        <Route
          path="/payables"
          element={
            <RequirePermission permissions={['payable:read']}>
              <PayablesPage />
            </RequirePermission>
          }
        />

        <Route
          path="/hr/employees"
          element={
            <RequirePermission permissions={['employee:read']}>
              <EmployeesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/payroll"
          element={
            <RequirePermission permissions={['payroll:read']}>
              <PayrollPage />
            </RequirePermission>
          }
        />
        <Route
          path="/payroll/import"
          element={
            <RequirePermission permissions={['payroll:create']}>
              <PayrollImportPage />
            </RequirePermission>
          }
        />
        <Route
          path="/payroll/:id"
          element={
            <RequirePermission permissions={['payroll:read']}>
              <PayrollDetailPage />
            </RequirePermission>
          }
        />

        <Route
          path="/payments"
          element={
            <RequirePermission permissions={['obligation:read']}>
              <PaymentQueuePage />
            </RequirePermission>
          }
        />
        <Route
          path="/payments/batches"
          element={
            <RequirePermission permissions={['payment_batch:read']}>
              <PaymentBatchesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/payments/batches/:id"
          element={
            <RequirePermission permissions={['payment_batch:read']}>
              <PaymentBatchDetailPage />
            </RequirePermission>
          }
        />

        <Route
          path="/banking/statements"
          element={
            <RequirePermission permissions={['bank_statement:read']}>
              <BankStatementsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/banking/transactions"
          element={
            <RequirePermission permissions={['bank_transaction:read']}>
              <BankTransactionsPage />
            </RequirePermission>
          }
        />

        <Route
          path="/reconciliation"
          element={
            <RequirePermission permissions={['reconciliation:read']}>
              <ReconciliationPage />
            </RequirePermission>
          }
        />

        <Route
          path="/reports"
          element={
            <RequirePermission permissions={['report:read']}>
              <ReportsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/audit"
          element={
            <RequirePermission permissions={['audit:read']}>
              <AuditPage />
            </RequirePermission>
          }
        />
        <Route path="/account" element={<AccountPage />} />
        {/* The handbook names no permission: it documents refusals as much as
            actions, so a user who cannot take a step still needs to read it.
            It is internal, though, so a vendor's own login is sent home. */}
        <Route
          path="/cookbook"
          element={
            <StaffOnly>
              <CookbookPage />
            </StaffOnly>
          }
        />
        <Route
          path="/notifications"
          element={
            <RequirePermission permissions={['notification:read']}>
              <NotificationsPage />
            </RequirePermission>
          }
        />

        <Route
          path="/settings/companies"
          element={
            <RequirePermission permissions={['company:read']}>
              <CompaniesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/groups"
          element={
            <RequirePermission permissions={['group:read']}>
              <GroupsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/regions"
          element={
            <RequirePermission permissions={['region:read']}>
              <RegionsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/verticals"
          element={
            <RequirePermission permissions={['vertical:read']}>
              <VerticalsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/business-units"
          element={
            <RequirePermission permissions={['business_unit:read']}>
              <BusinessUnitsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/locations"
          element={
            <RequirePermission permissions={['location:read']}>
              <LocationsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/departments"
          element={
            <RequirePermission permissions={['department:read']}>
              <DepartmentsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/vendors"
          element={
            <RequirePermission permissions={['vendor:read']}>
              <VendorsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/users"
          element={
            <RequirePermission permissions={['user:read']}>
              <UsersPage />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/roles"
          element={
            <RequirePermission permissions={['role:read']}>
              <RolesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/system"
          element={
            <RequirePermission permissions={['company:read']}>
              <SystemSettingsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/approvals"
          element={
            <RequirePermission permissions={['approval_rule:read']}>
              <ApprovalRulesPage />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/payment-accounts/:section?"
          element={
            <RequirePermission permissions={['payment_account:read', 'loan:read']}>
              <PaymentAccountsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/bank-accounts"
          element={<Navigate to="/settings/payment-accounts/bank-accounts" replace />}
        />
        <Route
          path="/settings/ledgers"
          element={
            <RequirePermission permissions={['ledger:read']}>
              <LedgersPage />
            </RequirePermission>
          }
        />
        <Route
          path="/settings/tds-sections"
          element={
            <RequirePermission permissions={['tds_section:read']}>
              <TdsSectionsPage />
            </RequirePermission>
          }
        />

        <Route path="*" element={<HomeRedirect />} />
      </Route>
    </Routes>
  );
}

/**
 * The landing page for whoever is signed in: the dashboard for a finance
 * role, a vendor's own invoices for a vendor login.
 */
function HomeRedirect() {
  const { canAny } = useAuth();
  return <Navigate to={homePath(canAny)} replace />;
}

/** Internal pages a vendor's own login is never shown. */
function StaffOnly({ children }: { children: ReactNode }) {
  const { canAny } = useAuth();
  if (isVendorLogin(canAny)) return <Navigate to={homePath(canAny)} replace />;
  return <>{children}</>;
}
